//import React, { useState, useEffect } from 'react';
import React, { useState, useEffect, useMemo } from "react";
import axios from 'axios';
import Paper from '@mui/material/Paper';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select'; // Import react-select
import {
    Checkbox, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, TableFooter
} from '@mui/material';
import { Tooltip } from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';


function DailySaleProfitReport() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [financialDate, setFinancialDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, permissions, token, username, logout } = useAuth();
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        setFinancialDate(getFormattedStartFinancialDate());
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        let year = today.getFullYear();

        if (today.getMonth() + 1 > 3) {
            // If the current month is greater than 3, use the current year
            return `${year}-04-01`;
        } else {
            // If the current month is 3 or less, use the previous year
            return `${year - 1}-04-01`;
        }
    };

    const getFormattedStartFinancialDate = () => {
        const monthStartFinancialDate = new Date();
        monthStartFinancialDate.setDate(1);
        return monthStartFinancialDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    const handleFinancialDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFinancialDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataBill = dataBill.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const [isLoading, setIsLoading] = useState(false);
    const fetchTableData = async () => {
        setDataItem([]);
        setIsLoading(true);
        try {
            const response = await fetchData('ReportApi', `/DailySaleProfit/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === "1111") {
                setDataItem(response.response_data);
            } else {
                console.warn("Unexpected response code:", response.responseCode);
            }
        } catch (error) {
            console.error("Error fetching Trial Balance Details:", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTableData();
    }, []);

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Daily Profit Sale Details');

        const now = new Date();
        // const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const formattedDateTime = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1)
            .toString()
            .padStart(2, '0')}/${now.getFullYear()} ${now.toLocaleTimeString()}`;
        const headerCaption = `Sale Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)} | Generated On: ${formattedDateTime} By : ${username}`;

        // 🟩 Add header caption
        worksheet.mergeCells('A1:F1');
        const headerCell = worksheet.getCell('A1');
        headerCell.value = headerCaption;
        headerCell.font = { bold: true, size: 12 };
        headerCell.alignment = { horizontal: 'center' };

        // 🟦 Add column headers
        const headerRow = worksheet.addRow(['Bill Date', 'Bill No', 'Name', 'Bill Amount', 'Purchase Amount', 'Profit']);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFDDEBF7' }, // Light blue header background
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        // 🧾 Add data rows
        filteredDataItem.forEach((rowData) => {
            worksheet.addRow([
                formatDate(rowData.BillDate),
                rowData.BillNumber,
                rowData.CustomerName,
                parseFloat(rowData.BillAmount) || 0,
                parseFloat(rowData.PurchaseAmount) || 0,
                parseFloat(rowData.ProfitAmount) || 0
            ]);
        });

        // ➕ Totals
        const totalBillAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.BillAmount) || 0), 0);
        const totalPurchaseAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.PurchaseAmount) || 0), 0);
        const totalProfitAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.ProfitAmount) || 0), 0);

        const totalRow = worksheet.addRow(['', '', 'Total', totalBillAmount, totalPurchaseAmount, totalProfitAmount]);
        totalRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
        });

        // 🧱 Auto column width
        worksheet.columns.forEach((col) => {
            col.width = 20;
        });

        // 💾 Export Excel
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DailyProfitSale.xlsx');
    };
    // const handleExportExcel = () => {
    //     const excelData = [];
    //     const now = new Date();
    //     const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    //     // Header caption for the date range
    //     const headerCaption = [`Sale Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)} | Generated On: ${formattedDateTime} By : ${username}`];
    //     excelData.push(headerCaption);

    //     // Column headers
    //     const headerRow = ['Bill Date', 'Bill No', 'Name', 'Bill Amount', 'Purchase Amount', 'Profit'];
    //     excelData.push(headerRow);

    //     // Add data rows
    //     filteredDataItem.forEach((rowData) => {
    //         const dataRow = [
    //             formatDate(rowData.BillDate),
    //             rowData.BillNumber,
    //             rowData.CustomerName,
    //             parseFloat(rowData.BillAmount) || 0,
    //             parseFloat(rowData.PurchaseAmount) || 0,
    //             parseFloat(rowData.ProfitAmount) || 0
    //         ];
    //         excelData.push(dataRow);
    //     });

    //     // Calculate totals
    //     const totalBillAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.BillAmount) || 0), 0);
    //     const totalPurchaseAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.PurchaseAmount) || 0), 0);
    //     const totalProfitAmount = filteredDataItem.reduce((sum, row) => sum + (parseFloat(row.ProfitAmount) || 0), 0);

    //     // Append totals row
    //     const totalsRow = [
    //         '', // Empty cells for non-numeric columns
    //         '',
    //         'Total',
    //         totalBillAmount.toFixed(2),
    //         totalPurchaseAmount.toFixed(2),
    //         totalProfitAmount.toFixed(2)
    //     ];
    //     excelData.push(totalsRow);

    //     // Create and export the Excel sheet
    //     const ws = XLSX.utils.aoa_to_sheet(excelData);
    //     const wb = XLSX.utils.book_new();
    //     XLSX.utils.book_append_sheet(wb, ws, 'Daily Profit Sale Details');
    //     XLSX.writeFile(wb, 'DailyProfitSale.xlsx');
    // };

    const [page, setPage] = useState(0); // Current page
    const [rowsPerPage, setRowsPerPage] = useState(15); // Rows per page

    // Define initial column order
    const [columnOrder, setColumnOrder] = useState([
        'Bill Date',
        'Bill No',
        'Name',
        'Bill Amount',
        'Purchase Amount',
        'Profit',
    ]);

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    // Function to handle drag start
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('columnIndex', index);
    };

    // Function to handle drag over
    const handleDragOver = (e) => {
        e.preventDefault(); // Allow drop
    };

    // Function to handle drop
    const handleDrop = (e, index) => {
        const draggedIndex = e.dataTransfer.getData('columnIndex');
        if (draggedIndex !== index) {
            const newOrder = [...columnOrder];
            // Swap columns
            [newOrder[draggedIndex], newOrder[index]] = [newOrder[index], newOrder[draggedIndex]];
            setColumnOrder(newOrder);
        }
    };

    // Process and paginate the data
    const processedData = filteredDataItem.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <div
            className="w-full mx-auto"
            style={{
                width: "100%",
                marginTop: "0",
                background: "#fff",
                border: "1px solid #dcdcdc",
                borderRadius: "6px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            }}
        >
            {/* ===== Report Header ===== */}
            <div
                style={{
                    background: "whitesmoke",
                    color: "black",
                    padding: "5px 10px",
                    borderTopLeftRadius: "6px",
                    borderTopRightRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "bold",
                    letterSpacing: "0.5px",
                    marginTop: "-30px",
                    height: "30px",
                }}
            >
                DAILY SALE PROFIT
            </div>

            {/* ===== Filter Section ===== */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 15px",
                    borderBottom: "1px solid #eee",
                    background: "#fafafa",
                }}
            >
                {/* Search */}
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearch}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    style={{
                        width: isFocused ? "260px" : "200px",
                        height: "30px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "0 10px",
                        transition: "width 0.3s",
                        outline: "none",
                        fontSize: "13px",
                        marginLeft: "-15px",
                    }}
                />

                {/* Dates + Buttons */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {/* From Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>From</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label style={{ fontSize: "11px", color: "#555" }}>To</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                fontSize: "12px",
                                height: "28px",
                                marginLeft: "5px",
                            }}
                        />
                    </div>

                    {/* Refresh */}
                    <Button
                        onClick={fetchTableData}
                        style={{
                            background: "#2f4050",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                            marginRight: "10px",
                        }}
                    >
                        Refresh
                    </Button>

                    {/* Excel */}
                    <Button
                        onClick={handleExportExcel}
                        style={{
                            background: "#00695c",
                            color: "white",
                            borderRadius: "4px",
                            padding: "5px 12px",
                            fontSize: "13px",
                            fontWeight: "bold",
                            height: "28px",
                            marginRight: "10px",
                        }}
                    >
                        Excel
                    </Button>
                </div>
            </div>

            {/* ===== Table Section ===== */}
            <div
                className="w-full mb-5 flex"
                style={{ maxHeight: "540px", overflowY: "auto", marginTop: "-0.70%" }}
            >
                <TableContainer component={Paper}>
                    <Table stickyHeader>
                        {/* Table Header */}
                        <TableHead sx={{ backgroundColor: "#484743", width: "2500px" }}>
                            <TableRow>
                                {columnOrder.map((col, index) => (
                                    <TableCell
                                        key={index}
                                        style={{
                                            background: "#e9ecef",
                                            fontWeight: "bold",
                                            fontSize: "11px",
                                            padding: "2px",
                                            borderBottom: "2px solid #ccc",
                                            textTransform: "uppercase",
                                            textAlign: ["Bill Amount", "Purchase Amount", "Profit"].includes(col)
                                                ? "right"
                                                : "left",
                                        }}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, index)}
                                        onDragOver={handleDragOver}
                                        onDrop={(e) => handleDrop(e, index)}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Table Body */}
                        <TableBody>
                            {processedData
                                .filter((rowData) => rowData.CustomerName !== "Total")
                                .map((rowData, index) => {
                                    const rowValues = {
                                        "Bill Date": rowData.BillDate ? formatDate(rowData.BillDate) : "",
                                        "Bill No": rowData.BillNumber || "",
                                        Name: rowData.CustomerName || "",
                                        "Bill Amount": rowData.BillAmount ? parseFloat(rowData.BillAmount).toFixed(2) : "0.00",
                                        "Purchase Amount": rowData.PurchaseAmount ? parseFloat(rowData.PurchaseAmount).toFixed(2) : "0.00",
                                        Profit: rowData.ProfitAmount ? parseFloat(rowData.ProfitAmount).toFixed(2) : "0.00",
                                    };

                                    return (
                                        <TableRow
                                            key={rowData.Id || index}
                                            sx={{
                                                backgroundColor: rowData.CustomerName === "Master" ? "#D3F1DF" : "transparent",
                                            }}
                                        >
                                            {columnOrder.map((col, idx) => (
                                                <TableCell
                                                    key={idx}
                                                    sx={{
                                                        borderBottom: "1px solid #E4E0E1",
                                                        padding: "5px",
                                                        textAlign: ["Bill Amount", "Purchase Amount", "Profit"].includes(col)
                                                            ? "right"
                                                            : "left",
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {rowValues[col]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>

                        {/* Table Footer */}
                        {/* <TableFooter> */}
                        {/* Total Row */}
                        <TableRow>
                            <TableCell colSpan={columnOrder.length - 3} sx={{ fontWeight: "bold", padding: "5px", fontSize: '12.5px', color: 'black' }}>
                                Total
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right", padding: "5px", fontSize: '12.5px', color: 'black' }}>
                                {processedData
                                    .filter((rowData) => rowData.CustomerName !== "Total")
                                    .reduce((sum, rowData) => sum + (parseFloat(rowData.BillAmount) || 0), 0)
                                    .toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right", padding: "5px", fontSize: '12.5px', color: 'black' }}>
                                {processedData
                                    .filter((rowData) => rowData.CustomerName !== "Total")
                                    .reduce((sum, rowData) => sum + (parseFloat(rowData.PurchaseAmount) || 0), 0)
                                    .toFixed(2)}
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", textAlign: "right", padding: "5px", fontSize: '12.5px', color: 'black' }}>
                                {processedData
                                    .filter((rowData) => rowData.CustomerName !== "Total")
                                    .reduce((sum, rowData) => sum + (parseFloat(rowData.ProfitAmount) || 0), 0)
                                    .toFixed(2)}
                            </TableCell>
                        </TableRow>

                        {/* Pagination Row */}
                        {/* <TableRow>
                                <TableCell colSpan={columnOrder.length}>
                                    <TablePagination
                                        rowsPerPageOptions={[15, 25, 50, 100]}
                                        count={processedData.filter((row) => row.CustomerName !== "Total").length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handlePageChange}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        component="div"
                                        labelRowsPerPage="Rows per page:"
                                        sx={{
                                            ".MuiTablePagination-toolbar": {
                                                padding: "0px 5px",
                                                minHeight: "35px",
                                            },
                                        }}
                                    />
                                </TableCell>
                            </TableRow> */}
                        {/* </TableFooter> */}
                    </Table>
                </TableContainer>
            </div>
        </div>
    );


    function descendingComparator(a, b, orderBy) {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }
        if (b[orderBy] > a[orderBy]) {
            return 1;
        }
        return 0;
    }

    function getComparator(order, orderBy) {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    }

    function stableSort(array, comparator) {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        return stabilizedThis.map((el) => el[0]);
    }




}
export default DailySaleProfitReport;